import { useCallback, useMemo, useRef, useState } from 'react';
import type { Editor } from '@tiptap/react';
import { exitSuggestion } from '@tiptap/suggestion';
import { useTranslation } from 'react-i18next';
import type { TextBuildingBlock } from 'types/textBuildingBlocks';
import {
  templateSuggestionPluginKey,
  type TemplateSuggestionBridge,
  type TemplateSuggestionProps,
} from './templateSuggestion';

interface Params {
  section: string;
  editor: Editor;
  textBuildingBlocks: TextBuildingBlock[];
  loadError: boolean;
  onInserted?: (fromPos: number) => void;
}

export function useTextBuildingBlockAutocomplete({
  section,
  editor,
  textBuildingBlocks,
  loadError,
  onInserted,
}: Params) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [items, setItems] = useState<TextBuildingBlock[]>([]);
  const [activeIndex, setActiveIndexState] = useState(0);

  // The Suggestion plugin fires outside React, so mirror the live values it needs into refs.
  const itemsRef = useRef<TextBuildingBlock[]>([]);
  const activeIndexRef = useRef(0);
  const commandRef = useRef<((block: TextBuildingBlock) => void) | null>(null);
  const clientRectRef = useRef<(() => DOMRect | null) | null>(null);
  const sectionRef = useRef(section);
  sectionRef.current = section;
  const blocksRef = useRef(textBuildingBlocks);
  blocksRef.current = textBuildingBlocks;
  const onInsertedRef = useRef(onInserted);
  onInsertedRef.current = onInserted;

  const setActiveIndex = useCallback((index: number) => {
    activeIndexRef.current = index;
    setActiveIndexState(index);
  }, []);

  const bridge = useMemo<TemplateSuggestionBridge>(() => {
    const filterBlocks = (query: string) => {
      const q = query.toLowerCase();
      return blocksRef.current.filter(
        (block) =>
          block.sections.includes(sectionRef.current) &&
          block.abbreviation.toLowerCase().includes(q)
      );
    };

    const apply = (props: TemplateSuggestionProps) => {
      const list = filterBlocks(props.query);
      itemsRef.current = list;
      activeIndexRef.current = 0;
      commandRef.current = props.command;
      clientRectRef.current = props.clientRect ?? null;
      setItems(list);
      setActiveIndexState(0);
    };

    return {
      items: filterBlocks,
      onStart: (props) => {
        apply(props);
        setIsOpen(true);
      },
      onUpdate: apply,
      onKeyDown: (event) => {
        const list = itemsRef.current;
        if (event.key === 'ArrowDown') {
          if (list.length === 0) return false;
          event.preventDefault();
          setActiveIndex((activeIndexRef.current + 1) % list.length);
          return true;
        }
        if (event.key === 'ArrowUp') {
          if (list.length === 0) return false;
          event.preventDefault();
          setActiveIndex((activeIndexRef.current - 1 + list.length) % list.length);
          return true;
        }
        if (event.key === 'Enter') {
          const block = list[activeIndexRef.current];
          if (!block) return false;
          event.preventDefault();
          commandRef.current?.(block);
          return true;
        }
        return false;
      },
      onExit: () => {
        setIsOpen(false);
        setItems([]);
        itemsRef.current = [];
        commandRef.current = null;
        clientRectRef.current = null;
      },
      onInserted: (fromPos) => onInsertedRef.current?.(fromPos),
    };
  }, [setActiveIndex]);

  const message = !isOpen
    ? null
    : loadError
      ? t('recording.notes.textBuildingBlocks.loadError')
      : items.length === 0
        ? t('recording.notes.textBuildingBlocks.empty')
        : null;

  const select = useCallback((block: TextBuildingBlock) => commandRef.current?.(block), []);

  const getAnchorRect = useCallback(() => clientRectRef.current?.() ?? null, []);

  const close = useCallback(() => {
    if (!editor.view.isDestroyed) exitSuggestion(editor.view, templateSuggestionPluginKey);
  }, [editor]);

  return {
    isOpen,
    items,
    activeIndex,
    message,
    getAnchorRect,
    select,
    setActiveIndex,
    close,
    bridge,
  };
}
